/**
 * Airwallex 动态表单 schema 的类型与纯逻辑。
 *
 * 为什么要动态：收款人的必填字段随「国家 × 币种 × 汇款方式 × 清算系统 × 主体类型」变化 ——
 * 美国 ACH 要 routing number，欧元区要 IBAN，SWIFT 要 SWIFT code。字段集合由 Airwallex
 * 维护并跟着各国清算规则更新，硬编码表单等于把那份维护成本接过来。
 */

export type FieldType = 'INPUT' | 'SELECT' | 'RADIO' | 'DYNAMIC_SELECT' | 'TRANSFER_METHOD';

export interface SchemaOption {
  label: string;
  value: string;
  description?: string;
}

export interface DynamicOptions {
  query_url: string;
  query_params: Array<{ name: string; required: boolean; value_of: string; pattern?: string }>;
}

export interface SchemaField {
  path: string;
  required: boolean;
  /** false = 不在界面上显示。⚠️ 不等于不用提交，见 collectSubmitValues。 */
  enabled: boolean;
  field: {
    key: string;
    type: FieldType;
    label: string;
    placeholder?: string;
    description?: string;
    tip?: string;
    example?: string;
    default?: string;
    options?: SchemaOption[];
    dynamic_options?: DynamicOptions;
    /**
     * Airwallex 给的提示：这个字段可能影响 schema 的形状。
     *
     * ⚠️ **不是「一变就重拉」的开关。** 它也被标在自由输入框上（邮编就是其一），
     * 照字面执行会变成每敲一个字符一轮请求。真正的判据见 shouldRefreshSchema。
     */
    refresh: boolean;
  };
  rule?: { type?: string; pattern?: string };
}

export interface FormSchema {
  condition: Record<string, string>;
  fields: SchemaField[];
  key?: string;
}

/**
 * 🔴 **同一个逻辑字段，Airwallex 会用两个不同的 path。**
 *
 * 汇款方式在条件不完整时叫 `transfer_methods`（复数、options 为空），一旦
 * `transfer_method` 进了 condition 就变成 `transfer_method`（单数）。实测：
 *
 * ```
 * condition {}                              → path=transfer_methods  opts=0
 * condition {entity,country,currency}       → path=transfer_methods  opts=2
 * condition {entity,country,currency,方式}  → path=transfer_method   opts=2   字段数 4→18
 * ```
 *
 * 不做归一的后果很隐蔽：用户选了 LOCAL，但因为值存在 `transfer_methods` 名下，
 * conditionFrom 取不到它 → 条件永远不完整 → schema 永远停在 4 个字段，
 * 界面上表现为「选了汇款方式却什么都没发生」。
 */
const PATH_ALIASES: Record<string, string> = {
  transfer_methods: 'transfer_method',
};

/** 把别名 path 归一成规范 path。 */
export function canonicalPath(path: string): string {
  return PATH_ALIASES[path] ?? path;
}

/** 决定 schema 形状的那几个字段。它们的值要回传给 /payees/schema 才能拿到下一版 schema。 */
/**
 * 🔴 **依赖条件**：合法取值由其它条件决定，上游任一条件一变它就可能失效。
 *
 * `local_clearing_system` 实测（2026-09-20，生产沙箱）：
 *   US/USD/LOCAL   → ACH / FEDWIRE / FEDNOW
 *   US/USD/SWIFT   → 字段**不存在**
 *   DE/EUR/LOCAL   → 字段**不存在**
 *
 * 带着旧值去请求下一版 schema，Airwallex 回 `SCHEMA_DEFINITION_NOT_FOUND`，
 * 整个表单当场卡死。所以重新拉 schema 的第一轮要把它排除，让新 schema 自己说了算；
 * 值本身不丢 —— 新 schema 里若仍有这个字段，mergeValues 会把用户选的值带回来，
 * 下一轮再作为条件发出去（见 Payees.tsx 的 refreshSchema）。
 */
export const DEPENDENT_CONDITION_PATHS = new Set([
  'beneficiary.bank_details.local_clearing_system',
]);

export const CONDITION_PATHS: Record<string, string> = {
  'beneficiary.entity_type': 'entity_type',
  'beneficiary.bank_details.bank_country_code': 'bank_country_code',
  'beneficiary.bank_details.account_currency': 'account_currency',
  'beneficiary.bank_details.local_clearing_system': 'local_clearing_system',
  'beneficiary.address.country_code': 'country_code',
  transfer_method: 'transfer_method',
};

export type FormValues = Record<string, string>;

/** 从一份 schema 里取所有字段的默认值，用于初始化或补齐。 */
export function defaultsOf(schema: FormSchema): FormValues {
  const out: FormValues = {};
  for (const f of schema.fields) {
    if (f.field.default) out[f.path] = f.field.default;
  }
  return out;
}

/**
 * schema 换了之后合并值：保留用户已填且新 schema 里仍存在的字段，其余用新默认值。
 *
 * 🔴 不能简单地「保留全部旧值」：换了国家之后，`account_routing_type1` 之类的字段可能
 * 已经不存在，原样带过去会被 Airwallex 拒；也不能「全部重置」，那样改一下币种就把
 * 地址、户名全清空，没法用。
 */
export function mergeValues(schema: FormSchema, previous: FormValues): FormValues {
  // 先按规范 path 建一张旧值索引，这样 transfer_methods ↔ transfer_method 换名时值不丢
  const byCanonical: FormValues = {};
  for (const [path, v] of Object.entries(previous)) {
    if (v !== undefined && v !== '') byCanonical[canonicalPath(path)] = v;
  }

  const next: FormValues = {};
  for (const f of schema.fields) {
    const old = previous[f.path] ?? byCanonical[canonicalPath(f.path)];
    if (old !== undefined && old !== '') next[f.path] = old;
    else if (f.field.default) next[f.path] = f.field.default;
  }
  return next;
}

/**
 * 新建收款人时把汇款方式预选成 SWIFT。
 *
 * 🔴 **判据是这一版 schema 下发的选项列表里有没有 SWIFT，不是我们维护一张「哪些组合
 * 支持 SWIFT」的表。** 后者违反 §4.1 不硬编码收款字段的原则——KRW 就不支持 SWIFT
 * （实测 Airwallex 对 KR × KRW × SWIFT 直接回 SCHEMA_DEFINITION_NOT_FOUND），而这类
 * 组合会随各国清算规则变化，我们没有能力也没有义务跟进。选项里没有 SWIFT 时原样沿用
 * Airwallex 下发的 default，绝不造一个选不中的值。
 *
 * 🔴 `touched` 为 true 时不覆盖：用户已经手动选过汇款方式（哪怕选的是 LOCAL），
 * 下一轮 schema 刷新就不能再把他的选择改回 SWIFT——否则表单会「自己动」。
 *
 * 🔴 **覆盖成 SWIFT 的同时必须清掉依赖旧汇款方式算出的条件字段**（见
 * DEPENDENT_CONDITION_PATHS，目前只有 local_clearing_system）。这里改的是同一类
 * 「上游条件一变，依赖它的字段就可能失效」——只是触发者从用户手动切换变成了我们自己。
 * 不清掉的话，`acc` 里会同时留着 `transfer_method=SWIFT` 和上一轮 LOCAL 下算出的
 * `local_clearing_system`，下一轮两者一起发给 Airwallex 必被拒
 * （SCHEMA_DEFINITION_NOT_FOUND）。清掉之后交给下一轮 schema 自己决定还要不要它，
 * 和 refreshSchema 里 round 0 排除依赖条件是同一个道理。
 */
export function preferSwift(schema: FormSchema, values: FormValues, touched = false): FormValues {
  if (touched) return values;
  const f = schema.fields.find((x) => canonicalPath(x.path) === 'transfer_method');
  if (!f) return values;
  const hasSwift = (f.field.options ?? []).some((o) => o.value === 'SWIFT');
  if (!hasSwift) return values;
  const next: FormValues = { ...values, [f.path]: 'SWIFT' };
  for (const path of Object.keys(next)) {
    if (DEPENDENT_CONDITION_PATHS.has(canonicalPath(path))) delete next[path];
  }
  return next;
}

/**
 * 提交给 /payees/schema 的条件参数。
 *
 * `skipDependent` 排除依赖条件（见 DEPENDENT_CONDITION_PATHS）——
 * 重新拉 schema 的第一轮用它，避免拿旧值去问一个已经不成立的组合。
 */
export function conditionFrom(
  values: FormValues,
  opts: { skipDependent?: boolean } = {},
): Record<string, string> {
  // 按规范 path 归一后再查表：值可能存在别名 path 下（见 PATH_ALIASES）
  const byCanonical: FormValues = {};
  for (const [path, v] of Object.entries(values)) {
    if (v) byCanonical[canonicalPath(path)] = v;
  }

  const q: Record<string, string> = {};
  for (const [path, param] of Object.entries(CONDITION_PATHS)) {
    if (opts.skipDependent && DEPENDENT_CONDITION_PATHS.has(path)) continue;
    const v = byCanonical[path];
    if (v) q[param] = v;
  }
  return q;
}

/**
 * 这次字段变更值不值得重新拉一版 schema。
 *
 * 🔴 **不能照字面执行字段上的 `refresh` 标记。** Airwallex 把 `refresh:true` 也标在
 * 自由输入框上 —— `beneficiary.address.postcode`（US 下 label 是 `ZIP code`）实测就是
 * `type=INPUT, refresh=true`。「一变就重拉」在下拉框上是对的（一次选择一条请求），
 * 落到输入框上就变成**每敲一个字符一整轮 schema 请求**；而 refreshSchema 拉回来之后会
 * `setValues` 把「发请求那一刻」的快照写回表单，期间敲进去的字符被抹掉 ——
 * 表现就是邮编根本打不进去。
 *
 * 而这一轮请求**必然是空转**：schema 长什么样只由我们发出去的那几个条件参数
 * （CONDITION_PATHS）决定，邮编不在其中。2026-09-20 对 sandbox 实测：同一组条件请求两次，
 * 回来的是字节完全相同的一份；把 postcode / bank_account_category / swift_code 当条件发过去，
 * 字段集合也一个不差，变的只是该字段自己的 `default` 被原样回显。
 *
 * 所以判据是**「这次改动有没有改变我们要发出去的条件」**，而不是字段上的标记。
 * 顺带也挡掉了「下拉里重新选中同一个值」这种同样打空转的情况。
 */
export function shouldRefreshSchema(before: FormValues, after: FormValues): boolean {
  return JSON.stringify(conditionFrom(before)) !== JSON.stringify(conditionFrom(after));
}

/**
 * 组装真正要提交的值。
 *
 * 🔴 **`enabled:false` 但 `required:true` 的字段必须带上它的 default。**
 * 这是自建表单最容易漏的一条：美国 ACH 的 `account_routing_type1` 就是
 * `enabled:false, required:true, default:"aba"` —— 界面上没有它，但不提交就会被
 * Airwallex 拒。「不显示」和「不需要」是两回事。
 */
export function collectSubmitValues(schema: FormSchema, values: FormValues): FormValues {
  const out: FormValues = {};
  for (const f of schema.fields) {
    const v = values[f.path];
    if (v !== undefined && v !== '') {
      out[f.path] = v;
    } else if (!f.enabled && f.required && f.field.default) {
      out[f.path] = f.field.default;
    }
  }
  return out;
}

/** 界面上要渲染的字段。 */
export function visibleFields(schema: FormSchema): SchemaField[] {
  return schema.fields.filter((f) => f.enabled);
}

export interface FieldError {
  path: string;
  message: string;
}

/**
 * 本地校验（必填 + 正则）。只是为了少打一次网络往返，**权威校验永远是服务端的
 * /payees/validate**（它打 Airwallex 的 validate 接口）—— 本地规则再全也追不上上游。
 */
export function validateLocally(schema: FormSchema, values: FormValues): FieldError[] {
  const errors: FieldError[] = [];
  for (const f of visibleFields(schema)) {
    const v = (values[f.path] ?? '').trim();
    if (f.required && !v) {
      // 字段名是 Airwallex 下发的英文（见 PRODUCT.md §4.1），用「」括起来读起来才不黏连：
      // 「请填写State」→「请填写「State」」
      errors.push({ path: f.path, message: `请填写「${f.field.label}」` });
      continue;
    }
    if (!v) continue;
    const pattern = f.rule?.pattern;
    if (pattern) {
      let re: RegExp | null = null;
      try {
        re = new RegExp(pattern);
      } catch {
        // Airwallex 的正则偶有 JS 不支持的语法；这种情况放行，交给服务端判
        re = null;
      }
      if (re && !re.test(v)) {
        errors.push({ path: f.path, message: `「${f.field.label}」格式不正确` });
      }
    }
  }
  return errors;
}

/**
 * 同一张表单里出现重名标签时，给它们加上字段 key 做区分。
 *
 * 实际遇到的：美国 ACH 的表单里 `transfer_method` 和 `local_clearing_system` 两个字段
 * 下发的 label 都是 `Transfer methods`，界面上就是两个一模一样的标题、两个不同的下拉，
 * 填的人只能靠猜。Airwallex 那边怎么下发我们管不了，但可以在显示时补一句区分。
 */
export function displayLabels(fields: SchemaField[]): Record<string, string> {
  const seen = new Map<string, number>();
  for (const f of fields) {
    const l = f.field.label || f.field.key;
    seen.set(l, (seen.get(l) ?? 0) + 1);
  }
  const out: Record<string, string> = {};
  for (const f of fields) {
    const base = f.field.label || f.field.key;
    const key = f.field.key.replace(/_/g, ' ');
    out[f.path] = (seen.get(base) ?? 0) > 1 && key !== base.toLowerCase() ? `${base}（${key}）` : base;
  }
  return out;
}
